import {
    GetFundingRequirementsCreditFacilityResponse,
    GetFundingRequirementsFlexi24Response,
    GetFundingRequirementsIwocapayResponse,
    GetFundingRequirementsMerchantCashAdvanceResponse
} from '@iwoca/lapi-client/edge';
import _ from 'lodash';

export type TDeserialisedFundingRequirements = {
    type:
        | 'DEBIT_CARD'
        | 'BANK_DETAILS'
        | 'PENNY_TRANSFER'
        | 'IDENTITY_QUESTION'
        | 'ONFIDO_VERIFICATION'
        | 'ONFIDO_VIDEO_VERIFICATION'
        | 'IDVERSE_VERIFICATION'
        | 'MAJOR_SHAREHOLDER_CHECK'
        | 'BIP'
        | 'APPLICANT_CALL'
        | 'COMMISSION_CONSENT'
        | 'UPLOAD_GEWERBESCHEIN';
    status: 'OPTIONAL' | 'REQUIRED' | 'COMPLETED' | 'ERROR' | 'PENDING';
}[];

export type TFundingRequirements =
    | GetFundingRequirementsCreditFacilityResponse
    | GetFundingRequirementsFlexi24Response
    | GetFundingRequirementsIwocapayResponse
    | GetFundingRequirementsMerchantCashAdvanceResponse;

type TFundingRequirement =
    | GetFundingRequirementsCreditFacilityResponse['data'][0]
    | GetFundingRequirementsFlexi24Response['data'][0]
    | GetFundingRequirementsIwocapayResponse['data'][0]
    | GetFundingRequirementsMerchantCashAdvanceResponse['data'][0];

function getActionFromFundingRequirement(
    funding_requirement: TFundingRequirement
): TDeserialisedFundingRequirements[number]['type'] {
    switch (funding_requirement.recommended_resolution_method) {
        case 'add_default_payment_method':
            return 'DEBIT_CARD';
        case 'link_bank_account':
        case 'check_linked_direct_debit':
        case 'check_funding_bank_account_is_valid':
            return 'BANK_DETAILS';
        case 'complete_penny_transfer':
        case 'complete_penny_transfer_with_signature':
            return 'PENNY_TRANSFER';
        case 'onfido_check':
            return 'ONFIDO_VERIFICATION';
        case 'onfido_video_check':
            return 'ONFIDO_VIDEO_VERIFICATION';
        case 'idverse_check':
            return 'IDVERSE_VERIFICATION';
        case 'major_shareholders_check':
            return 'MAJOR_SHAREHOLDER_CHECK';
        case 'check_customer_does_not_exceed_bip_cap':
            return 'BIP';
        case 'check_commission_consent_checkbox':
            return 'COMMISSION_CONSENT';
        case 'gewerbeschein_hochladen':
            return 'UPLOAD_GEWERBESCHEIN';
        default:
            return 'APPLICANT_CALL';
    }
}

function showUniqueFundingRequirementsWithCorrectStatus(
    fundingRequirements: TDeserialisedFundingRequirements
): TDeserialisedFundingRequirements {
    return fundingRequirements.reduce((requirements: any, requirement: any) => {
        const existingDuplicateRequirement = _.findIndex(requirements, {
            type: requirement.type
        });
        if (existingDuplicateRequirement === -1) {
            return [...requirements, requirement];
        }

        const STATUS_PRIORITY_ORDER = {
            REQUIRED: 1,
            ERROR: 2,
            PENDING: 3,
            COMPLETED: 4
        };

        if (
            STATUS_PRIORITY_ORDER[
                requirements[existingDuplicateRequirement]
                    .status as keyof typeof STATUS_PRIORITY_ORDER
            ] >
            STATUS_PRIORITY_ORDER[
                requirement.status as keyof typeof STATUS_PRIORITY_ORDER
            ]
        ) {
            requirements[existingDuplicateRequirement] = requirement;
        }

        return requirements;
    }, []);
}

const completeApplicationStatus = {
    optional: 'OPTIONAL',
    required: 'REQUIRED',
    completed: 'COMPLETED',
    error: 'ERROR',
    pending: 'PENDING'
};

const getCompleteApplicationStatus = (
    requirement: GetFundingRequirementsCreditFacilityResponse['data'][0]
) => {
    if (requirement.status === 'required' && requirement.is_optional === true) {
        return completeApplicationStatus.optional;
    }

    return completeApplicationStatus[requirement.status];
};

export const deserialiseFundingRequirements = (
    funding_requirements: TFundingRequirements['data']
): TDeserialisedFundingRequirements => {
    const deserialisedFundingRequirements = funding_requirements
        .filter(
            (requirement) =>
                requirement.category !== 'contractual' ||
                (requirement.category === 'contractual' &&
                    [
                        'complete_repayment_pledge',
                        'check_commission_consent_checkbox'
                    ].includes(requirement.recommended_resolution_method))
        )
        .filter((req) => !req.is_optional)
        .map((funding_requirement) => ({
            type: getActionFromFundingRequirement(
                funding_requirement
            ) as TDeserialisedFundingRequirements[0]['type'],
            status: getCompleteApplicationStatus(
                funding_requirement
            ) as TDeserialisedFundingRequirements[0]['status']
        }));

    const uniqueFundingRequirements =
        showUniqueFundingRequirementsWithCorrectStatus(
            deserialisedFundingRequirements
        );

    return uniqueFundingRequirements;
};
